import { computed, effect, inject, untracked } from '@angular/core';
import {
  buildFactoryViewSectionNavItems,
  buildLocationLine,
  collectIndustrialSubtypeIds,
  gateVisibleFactory,
  hasAnyCapabilityLeaves,
  hasFactoryBusinessData,
  hasFactoryCapabilities,
  resolveCountryCode,
  resolveCountryName,
  scheduleFactoryViewSectionScrollSpy,
  scrollToFactoryViewSection,
  type FactoryViewSectionId,
  type FactoryViewSectionNavItem,
} from '@indutrust/factory-util';
import { CountryStore } from '@indutrust/shared-data';
import {
  patchState,
  signalStore,
  withComputed,
  withHooks,
  withMethods,
  withState,
} from '@ngrx/signals';
import { injectDispatch } from '@ngrx/signals/events';
import { factoryDocumentCrudEvents } from '../../document/+state/factory-documents.events';
import { FactoryDocumentsStore } from '../../document/+state/factory-documents.store';
import { FactoryStore } from '../../factory/factory.store';
import { machineParkEvents } from '../../machine-park/+state/machine-park.events';
import { MachineParkStore } from '../../machine-park/+state/machine-park.store';
import { factoryProjectReferenceCrudEvents } from '../../project-reference/+state/factory-project-references.events';
import { FactoryProjectReferencesStore } from '../../project-reference/+state/factory-project-references.store';
import { virtualTourEvents } from '../../virtual-tour/+state/virtual-tour.events';
import { VirtualTourStore } from '../../virtual-tour/+state/virtual-tour.store';

/**
 * Scoped store for the read-only factory profile view. Owns:
 *
 * - Visibility gate (`visibleFactory`) — only published + verified factories
 *   pass through (R14 in `docs/product/05-roles-and-permissions.md`).
 * - All derived display values (`loading`, `countryCode`, `countryName`,
 *   `locationLine`, `hasBusinessData`, `hasCapabilities`, `sectionNavItems`,
 *   `visibleDocuments`).
 * - Section navigation state (`activeSection`, `scrollToSection`).
 * - Sub-resource loading effects (machine park, virtual tour, documents,
 *   project references) — triggered once the visibility gate passes.
 * - Scroll-spy `IntersectionObserver` — keeps `activeSection` in sync with
 *   the section currently topmost in the viewport.
 *
 * @example
 * const viewStore = inject(FactoryViewUiStore);
 * viewStore.scrollToSection('capabilities');
 */
export const FactoryViewUiStore = signalStore(
  withState<{
    activeSection: FactoryViewSectionId | null;
    isAutoScrolling: boolean;
    scrollSeq: number;
  }>({
    activeSection: null,
    isAutoScrolling: false,
    scrollSeq: 0,
  }),
  withComputed(
    (
      store,
      factoryStore = inject(FactoryStore),
      countryStore = inject(CountryStore),
      documentsStore = inject(FactoryDocumentsStore),
      machineParkStore = inject(MachineParkStore),
      projectReferencesStore = inject(FactoryProjectReferencesStore),
      tourStore = inject(VirtualTourStore)
    ) => {
      const visibleFactory = computed(() =>
        gateVisibleFactory(factoryStore.entity())
      );

      const loading = computed(() => {
        const status = factoryStore.loadByIdStatus();
        return status === 'initial' || status === 'loading';
      });

      const countryName = computed(() => {
        const factory = visibleFactory();
        if (!factory) {
          return null;
        }
        return resolveCountryName(factory, countryStore.entities());
      });

      return {
        visibleFactory,
        loading,
        countryName,
        countryCode: computed(() => {
          const factory = visibleFactory();
          if (!factory) {
            return null;
          }
          return resolveCountryCode(factory, countryStore.entities());
        }),
        hasBusinessData: computed(() => {
          const factory = visibleFactory();
          return factory ? hasFactoryBusinessData(factory) : false;
        }),
        hasCapabilities: computed(() => {
          const factory = visibleFactory();
          if (!factory) {
            return false;
          }
          return (
            hasFactoryCapabilities(factory) ||
            hasAnyCapabilityLeaves(
              factory,
              ...projectReferencesStore.entities(),
              ...machineParkStore.entities()
            ) ||
            collectIndustrialSubtypeIds(projectReferencesStore.entities())
              .length > 0
          );
        }),
        locationLine: computed(() => {
          const factory = visibleFactory();
          if (!factory) {
            return null;
          }
          return buildLocationLine(factory, countryName());
        }),
        visibleDocuments: computed(() =>
          documentsStore.entities().filter((d) => d.status === 'validated')
        ),
        sectionNavItems: computed<FactoryViewSectionNavItem[]>(() => {
          const factory = visibleFactory();
          if (!factory) {
            return [];
          }
          const validatedDocs = documentsStore
            .entities()
            .filter((d) => d.status === 'validated');
          return buildFactoryViewSectionNavItems(factory, {
            documentsCount: validatedDocs.length,
            machineParkCount: machineParkStore.entities().length,
            projectReferencesCount: projectReferencesStore.entities().length,
            tourNodesCount: tourStore.nodes().length,
          });
        }),
      };
    }
  ),
  withMethods((store) => ({
    scrollToSection(section: FactoryViewSectionId): void {
      const seq = store.scrollSeq() + 1;
      patchState(store, {
        activeSection: section,
        isAutoScrolling: true,
        scrollSeq: seq,
      });
      scrollToFactoryViewSection(section)
        .then(() => {
          if (store.scrollSeq() === seq) {
            patchState(store, {
              isAutoScrolling: false,
            });
          }
        })
        .catch(() => undefined);
    },
    setActiveSection(section: FactoryViewSectionId | null): void {
      patchState(store, { activeSection: section });
    },
  })),
  withHooks((store) => {
    const docDispatch = injectDispatch(factoryDocumentCrudEvents);
    const machineParkDispatch = injectDispatch(machineParkEvents);
    const projectRefDispatch = injectDispatch(
      factoryProjectReferenceCrudEvents
    );
    const tourDispatch = injectDispatch(virtualTourEvents);

    return {
      onInit() {
        effect(() => {
          const factory = store.visibleFactory();
          if (factory) {
            untracked(() => {
              const factoryId = factory.id;
              tourDispatch.loadRequested({ factoryId });
              machineParkDispatch.loadRequested({ factoryId });
              docDispatch.loadAllRequested({ urlSegments: { factoryId } });
              projectRefDispatch.loadAllRequested({
                urlSegments: { factoryId },
              });
            });
          }
        });

        effect((onCleanup) => {
          const factory = store.visibleFactory();
          const navItems = store.sectionNavItems();
          if (!factory || navItems.length === 0) {
            return;
          }

          const handle = scheduleFactoryViewSectionScrollSpy(
            store,
            navItems[0]?.id ?? null
          );
          onCleanup(() => {
            handle.cancel();
          });
        });
      },
    };
  })
);
